import { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Switch, Table, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import { DataKitDto, DataKitGroupDto, DataKitItemDto } from "../../../../domain/DataKitDto";
import "./DataKitItemsTab.scss";
import { DataKitUsedOnLink } from "./DataKitMenuView";

const ALL_GROUPS = "__all__";

// Unit Separator (U+001F), the same key DataKitItemDrawer builds a group path with and for the
// same reason: a readable separator is a name someone could legitimately type, which would make
// two different nested paths encode alike. Kept as a char code rather than a literal control
// character so it survives diffs and tooling.
const PATH_SEP = String.fromCharCode(31);

const pathOfGroup = (group: DataKitGroupDto): string[] =>
  group.path && group.path.length > 0 ? group.path : [group.name];

const pathOfEntry = (entry: { groupName: string; groupPath?: string[] }): string[] =>
  entry.groupPath && entry.groupPath.length > 0 ? entry.groupPath : [entry.groupName];

const keyOfPath = (path: string[]) => path.join(PATH_SEP);

export function formatGroupEntry(entry: { groupName: string; groupPath?: string[] }): string {
  const path = entry.groupPath || [];
  return path.length > 0 ? path.join(" › ") : entry.groupName;
}

/**
 * Items no longer have a primary group, so the Group column lists every entry rather than picking
 * one. The first entry is what the canvas renders, so it is shown first and the rest follow.
 */
export function formatGroups(item: DataKitItemDto): string[] {
  return item.groups.map(formatGroupEntry);
}

export function formatPrices(item: DataKitItemDto): string {
  const prices = [...item.prices].sort((a, b) => a.sequence - b.sequence);
  const shown = prices.map((p) => p.price).filter((p) => !!p && p.trim() !== "");
  if (shown.length === 0) return "––";
  return shown.join(" / ");
}

/**
 * How many locations and menus price this item themselves.
 *
 * Counted as distinct locations and menus, not as override rows: an item with two sizes overridden
 * at one location is "1 location", not 2. The old chip read "3 ovr", which told a member neither
 * what was overridden nor by whom - and "ovr" is the internal word for it, which customer copy
 * never uses.
 */
export function overrideSummary(item: DataKitItemDto): string | null {
  const locations = new Set<string>();
  const menus = new Set<string>();
  item.prices.forEach((p) => {
    (p.overrides || []).forEach((o) => locations.add(o.unitId));
    (p.menuOverrides || []).forEach((o) => menus.add(o.menuId));
  });
  const parts: string[] = [];
  if (locations.size > 0) {
    parts.push(`${locations.size} location${locations.size === 1 ? "" : "s"}`);
  }
  if (menus.size > 0) parts.push(`${menus.size} menu${menus.size === 1 ? "" : "s"}`);
  return parts.length > 0 ? parts.join(" \u00b7 ") : null;
}

interface Props {
  kit: DataKitDto;
  items: DataKitItemDto[];
  groups: DataKitGroupDto[];
  archivedCount: number;
  showArchived: boolean;
  onToggleArchived: () => void;
  canWrite: boolean;
  /** A refresh is in flight - the table spins instead of showing its empty state. */
  loading?: boolean;
  onChanged: () => void;
  onEditItem: (item: DataKitItemDto) => void;
}

export function DataKitItemsTab({
  kit,
  items,
  groups,
  archivedCount,
  showArchived,
  onToggleArchived,
  canWrite,
  loading = false,
  onChanged,
  onEditItem,
}: Props) {
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>(ALL_GROUPS);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  /**
   * Availability flips the switch on click and reconciles afterwards.
   *
   * It used to render `item.available` straight from the prop and disable every switch in the
   * table until the write *and* the parent's six-endpoint reload had both landed - so a toggle sat
   * visibly dead for as long as that took. Availability is not staged (it applies the moment it is
   * saved) and the write itself is a single column update, so there is nothing here worth making
   * the member wait on.
   *
   * Entries are dropped by the effect below once the refetched item agrees, rather than on the
   * response: clearing them any earlier snaps the switch back to the stale prop for however long
   * the reload still has to run.
   */
  const [pendingAvailable, setPendingAvailable] = useState<Record<string, boolean>>({});
  const [availabilityBusy, setAvailabilityBusy] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelected([]);
  }, [showArchived]);

  // Reconcile the optimistic flips: once a reloaded item carries the value we already showed, the
  // override has nothing left to say and is dropped. Self-healing, so a failed or superseded
  // reload can never strand one.
  useEffect(() => {
    setPendingAvailable((current) => {
      const entries = Object.entries(current);
      if (entries.length === 0) return current;
      const next = Object.fromEntries(
        entries.filter(([id, value]) => {
          const item = items.find((i) => i.id === id);
          return !item || item.available !== value;
        })
      );
      return entries.length === Object.keys(next).length ? current : next;
    });
  }, [items]);

  const availabilityOf = (item: DataKitItemDto) =>
    item.id && item.id in pendingAvailable ? pendingAvailable[item.id] : item.available;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      // Prefix match on the encoded path, not equality: picking a parent is expected to bring its
      // subgroups with it, and an item filed only under "Food › Raw" must not vanish when the
      // member filters on "Food".
      if (
        groupFilter !== ALL_GROUPS &&
        !item.groups.some((g) => {
          const key = keyOfPath(pathOfEntry(g));
          return key === groupFilter || key.startsWith(`${groupFilter}${PATH_SEP}`);
        })
      ) {
        return false;
      }
      if (q && item.groups.some((g) => formatGroupEntry(g).toLowerCase().includes(q))) return true;
      if (!q) return true;
      return (
        (item.name || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q) ||
        item.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [items, query, groupFilter]);

  /**
   * One option per group in the kit, labelled and keyed by its full path.
   *
   * Groups nest, and a name is unique only within its parent, so the bare name this used to carry
   * was both ambiguous (two parents may each own a "Raw" - one option shadowed the other, and
   * picking either filtered on both) and unreadable (a nested group showed as a bare leaf with no
   * hint of where it lived). DataKitItemDrawer's own picker has always been path-keyed; this is
   * the same shape.
   */
  const groupOptions = useMemo(
    () => [
      { label: "All groups", value: ALL_GROUPS },
      ...groups
        .map((g) => {
          const path = pathOfGroup(g);
          return { label: path.join(" › "), value: keyOfPath(path) };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [groups]
  );

  /**
   * Distinct designs using anything in the current selection - what the delete confirm reports.
   * Asked of the server when the confirm opens rather than summed from the rows' own "Used on"
   * counts: one design placing several of the selected items is one design, and the sum counted it
   * once per item (QA, Oct 8: 22 items reading "1 menu" each confirmed as "29 designs").
   * `undefined` while the count is loading, `null` if it couldn't be fetched.
   */
  const [deletionUsage, setDeletionUsage] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    if (!confirmDelete || !kit.id || selected.length === 0) return;
    let cancelled = false;
    setDeletionUsage(undefined);
    DataKitApi.previewBulkDeleteItems(kit.id, { itemIds: selected })
      .then((res) => {
        if (!cancelled) setDeletionUsage(res.data?.designCount ?? 0);
      })
      .catch(() => {
        if (!cancelled) setDeletionUsage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [confirmDelete, kit.id, selected]);

  const setAvailability = (item: DataKitItemDto, available: boolean) => {
    if (!kit.id || !item.id) return;
    const id = item.id;
    // Flip first. `busy` is deliberately not touched - it disables every switch in the table plus
    // the bulk toolbar, which is the right shape for a bulk action and the wrong one for flipping
    // a single row.
    setPendingAvailable((current) => ({ ...current, [id]: available }));
    setAvailabilityBusy((current) => ({ ...current, [id]: true }));
    DataKitApi.setAvailability(kit.id, { itemIds: [id], available })
      .then(onChanged)
      .catch(() => {
        setPendingAvailable((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        });
        message.error("Could not change availability");
      })
      .finally(() =>
        setAvailabilityBusy((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        })
      );
  };

  const bulkArchive = () => {
    if (!kit.id || selected.length === 0) return;
    setBusy(true);
    DataKitApi.bulkArchiveItems(kit.id, { itemIds: selected })
      .then(() => {
        setSelected([]);
        setConfirmArchive(false);
        onChanged();
      })
      .catch(() => message.error("Could not archive the selected items"))
      .finally(() => setBusy(false));
  };

  const bulkDelete = () => {
    if (!kit.id || selected.length === 0) return;
    setBusy(true);
    DataKitApi.bulkDeleteItems(kit.id, { itemIds: selected })
      .then(() => {
        setSelected([]);
        setConfirmDelete(false);
        onChanged();
      })
      .catch(() => message.error("Could not delete the selected items"))
      .finally(() => setBusy(false));
  };

  const bulkRestore = () => {
    if (!kit.id || selected.length === 0) return;
    setBusy(true);
    DataKitApi.bulkRestoreItems(kit.id, { itemIds: selected })
      .then(() => {
        setSelected([]);
        onChanged();
      })
      .catch(() => message.error("Could not restore the selected items"))
      .finally(() => setBusy(false));
  };

  const bulkAvailability = (available: boolean) => {
    if (!kit.id || selected.length === 0) return;
    setBusy(true);
    const ids = [...selected];
    setPendingAvailable((current) => ({
      ...current,
      ...Object.fromEntries(ids.map((id) => [id, available])),
    }));
    DataKitApi.setAvailability(kit.id, { itemIds: ids, available })
      .then(() => {
        setSelected([]);
        onChanged();
      })
      .catch(() => {
        setPendingAvailable((current) => {
          const next = { ...current };
          ids.forEach((id) => delete next[id]);
          return next;
        });
        message.error("Could not change availability");
      })
      .finally(() => setBusy(false));
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      sorter: (a: DataKitItemDto, b: DataKitItemDto) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
      render: (_: string, item: DataKitItemDto) => (
        <div className="data-kit-item-name-cell">
          <div className="data-kit-item-name">{item.name || "Untitled"}</div>
          {item.description && (
            <div className="data-kit-item-description">{item.description}</div>
          )}
        </div>
      ),
    },
    {
      title: "Group",
      key: "group",
      width: 220,
      render: (_: unknown, item: DataKitItemDto) => {
        const entries = formatGroups(item);
        if (entries.length === 0) return <span className="data-kit-item-no-group">––</span>;
        return (
          <div className="data-kit-item-groups">
            {entries.map((label) => (
              <span key={label} className="data-kit-item-group-chip">
                {label}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Price",
      key: "price",
      width: 170,
      align: "center" as const,
      render: (_: unknown, item: DataKitItemDto) => {
        const summary = overrideSummary(item);
        return (
          <div className="data-kit-item-price-cell">
            <span>{formatPrices(item)}</span>
            {summary && <span className="data-kit-override-chip">{summary}</span>}
          </div>
        );
      },
    },
    {
      title: "Used on",
      key: "usedOn",
      width: 140,
      // Opens the menus using it - the same list everywhere "Used on N menus" appears (Oct 12).
      render: (_: unknown, item: DataKitItemDto) => (
        <DataKitUsedOnLink itemId={item.id} itemName={item.name} count={item.usedOnCount} />
      ),
    },
    {
      title: "Available",
      key: "available",
      width: 110,
      align: "right" as const,
      onHeaderCell: () => ({ title: "Applies immediately — no publish needed" }),
      render: (_: unknown, item: DataKitItemDto) => (
        <Switch
          className="data-kit-available-switch"
          size="small"
          checked={availabilityOf(item)}
          loading={!!item.id && !!availabilityBusy[item.id]}
          disabled={!canWrite || item.archived || (!!item.id && !!availabilityBusy[item.id])}
          onClick={(_checked, e) => e.stopPropagation()}
          onChange={(checked) => setAvailability(item, checked)}
        />
      ),
    },
  ];

  return (
    <div className="data-kit-items-tab">
      <div className="data-kit-items-toolbar">
        <Input
          className="data-kit-items-search"
          size="large"
          placeholder={`Search ${items.length} item${items.length === 1 ? "" : "s"}`}
          prefix={<SearchOutlined style={{ color: "#999" }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
        />
        <Select
          className="data-kit-group-filter"
          size="large"
          value={groupFilter}
          onChange={setGroupFilter}
          options={groupOptions}
        />
        <button type="button" className="data-kit-archived-link" onClick={onToggleArchived}>
          {showArchived ? "Back to active items" : `Archived • ${archivedCount}`}
        </button>
      </div>

      {selected.length > 0 && (
        <div className="data-kit-bulk-toolbar">
          <span className="data-kit-bulk-count">
            {selected.length} selected
          </span>
          <div className="data-kit-bulk-actions">
            {showArchived ? (
              <Button size="small" onClick={bulkRestore} disabled={busy}>
                Restore
              </Button>
            ) : (
              <>
                <Button size="small" onClick={() => bulkAvailability(true)} disabled={busy}>
                  Mark available
                </Button>
                <Button size="small" onClick={() => bulkAvailability(false)} disabled={busy}>
                  Mark 86'd
                </Button>
                <Button size="small" danger onClick={() => setConfirmArchive(true)} disabled={busy}>
                  Archive
                </Button>
                <Button size="small" danger onClick={() => setConfirmDelete(true)} disabled={busy}>
                  Delete…
                </Button>
              </>
            )}
            <Button size="small" type="text" onClick={() => setSelected([])}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <Modal className={"clean one"} open={confirmDelete} onCancel={() => setConfirmDelete(false)}>
        <header>
          <h2>
            Delete {selected.length} item{selected.length === 1 ? "" : "s"}?
          </h2>
        </header>
        <p className="data-kit-archive-copy">
          {deletionUsage === undefined ? (
            <>Checking which designs use {selected.length === 1 ? "this item" : "these items"}&hellip;</>
          ) : deletionUsage === null ? (
            // The count couldn't be fetched: say what happens without a number rather than guess one.
            <>
              Any design using {selected.length === 1 ? "this item" : "these items"} keeps the values it
              has today, but stops updating from this library and cannot be reconnected. Online menus
              and QR menus drop the items right away.
            </>
          ) : deletionUsage > 0 ? (
            <>
              {deletionUsage} design{deletionUsage === 1 ? "" : "s"} still{" "}
              {deletionUsage === 1 ? "uses" : "use"} what you are deleting. {deletionUsage === 1 ? "It" : "They"}{" "}
              keep the values {deletionUsage === 1 ? "it has" : "they have"} today, but stop updating
              from this library and cannot be reconnected. Online menus and QR menus drop the items
              right away.
            </>
          ) : (
            <>
              Nothing uses {selected.length === 1 ? "this item" : "these items"} today, so nothing on
              a design changes. Online menus and QR menus drop {selected.length === 1 ? "it" : "them"}{" "}
              right away.
            </>
          )}{" "}
          This cannot be undone &mdash; archive instead if you may want{" "}
          {selected.length === 1 ? "it" : "them"} back.
        </p>
        <footer>
          <Button size="large" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            loading={busy}
            onClick={() => {
              setConfirmDelete(false);
              setConfirmArchive(true);
            }}
          >
            Archive instead
          </Button>
          <Button
            size="large"
            className="primary"
            loading={busy}
            disabled={deletionUsage === undefined}
            onClick={bulkDelete}
          >
            Delete
          </Button>
        </footer>
      </Modal>

      <Table
        className="data-kit-items-table"
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        rowClassName="data-kit-item-row"
        loading={loading}
        pagination={false}
        rowSelection={
          canWrite
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as string[]),
              }
            : undefined
        }
        locale={{
          emptyText: showArchived
            ? "No archived items"
            : query.trim() || groupFilter !== ALL_GROUPS
            ? "No items match your filters"
            : "No items in this library yet",
        }}
        onRow={(item) => ({
          onClick: () => onEditItem(item),
        })}
      />

      <Modal
        className={"clean one"}
        open={confirmArchive}
        onCancel={() => setConfirmArchive(false)}
      >
        <header>
          <h2>
            Archive {selected.length} item{selected.length === 1 ? "" : "s"}?
          </h2>
        </header>
        <p className="data-kit-archive-copy">
          Archiving is staged: these items keep appearing on designs until you publish this library.
          To take something off a menu right now, switch it to unavailable instead — that applies
          immediately, without publishing anything else. You can restore archived items from the
          archived view.
        </p>
        <footer>
          <Button size="large" onClick={() => setConfirmArchive(false)}>
            Cancel
          </Button>
          <Button size="large" className="primary" loading={busy} onClick={bulkArchive}>
            Archive
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
